import React, { useEffect, useState } from 'react';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import { BRAND_IMAGES, getBrandImageUrl } from '@/app/common/contentHubAssets.ts';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import useCCMShown from '@/app/common/hooks/useCCMShown';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { ModalBody, ModalButtons, ModalImage, ModalPrimaryButton, ModalTitle } from './styled';
import type { Localization } from '../../../../common/Localization';
import getSearchParams from '../../../../common/getSearchParams';
import { getCookie, setCookie } from '../../../../utils/Cookies/clientCookies';
import { COOKIE_KEY } from '../../../../utils/Cookies/types';
import OpenXModal from '../../OpenXModal';

export type OvernightFlightsProps = {
  localization: Localization;
};

const OvernightFlightsModal = ({ localization }: OvernightFlightsProps) => {
  const query = getSearchParams();
  const { t } = useTranslation();
  const { isMobile } = useCommonClient();
  const { curiosityClickEvents } = useCuriosityManager();

  const { commonData } = usePageWrapperData();
  const isCCMShown = useCCMShown(commonData?.isLocal);
  const [showOvernightsModal, setShowOvernightsModal] = useState<boolean>(false);
  const overnightSelected = !!query.overnight_selected;

  const modalTitle = t('vacation.oss.overnight_flight.modal.title');
  const modalDesc = t('vacation.oss.overnight_flight.modal.desc');
  const modalCta = t('vacation.oss.overnight_flight.modal.cta');

  const image = getBrandImageUrl(BRAND_IMAGES.OVERNIGHT, localization);

  useEffect(() => {
    if (!isCCMShown) return;

    const hasOvernightFlightCookie = getCookie(COOKIE_KEY.OVERNIGHT_FLIGHTS_POPUP);
    if (!hasOvernightFlightCookie && !overnightSelected) {
      handleShowModal();
    }
  }, [isCCMShown]);

  const handleShowModal = () => {
    curiosityClickEvents.overnightFlights('popup_show');
    setShowOvernightsModal(true);
    setCookie(COOKIE_KEY.OVERNIGHT_FLIGHTS_POPUP, 'true');
  };

  const closeModal = () => {
    setShowOvernightsModal(false);
  };
  const handleCloseModal = () => {
    curiosityClickEvents.overnightFlights('popup_close');
    closeModal();
  };

  const handleConfirm = () => {
    curiosityClickEvents.overnightFlights('popup_confirm');
    closeModal();
  };

  return (
    <OpenXModal
      isOpen={showOvernightsModal}
      onClose={handleCloseModal}
      className="overnight-flights-modal"
      size={MODAL_SIZES.S}
      id="overnight_flights_modal"
      aria-label={modalTitle}
      style={{ textAlign: 'center' }}
      isMobile={isMobile}
      brand={localization.brand}
      header={{
        sticky: false,
      }}
      footer={{
        sticky: true,
        element: (
          <ModalButtons>
            <ModalPrimaryButton size="large" onClick={handleConfirm}>
              {modalCta}
            </ModalPrimaryButton>
          </ModalButtons>
        ),
      }}
    >
      <ModalImage src={image} alt={modalTitle} />
      <ModalTitle>{modalTitle}</ModalTitle>
      <ModalBody>{modalDesc}</ModalBody>
    </OpenXModal>
  );
};

export default OvernightFlightsModal;
